'use client';

import { enforceParagraphSpacing } from '@/utils/markdownSpacing';
import {
    extractInlineFontFamilies,
    getKnownGoogleFontWeights,
} from '@/utils/google-fonts';
import clsx from 'clsx';
import { contrastColor } from 'contrast-color';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';

function headingClasses(level) {
    switch (level) {
        case 'h1':
            return 'mb-4 mt-8 whitespace-pre-wrap font-semibold leading-tight first:mt-0';
        case 'h2':
            return 'mb-4 mt-8 whitespace-pre-wrap font-semibold leading-tight first:mt-0';
        case 'h3':
            return 'mb-3 mt-7 whitespace-pre-wrap font-semibold leading-tight first:mt-0';
        case 'h4':
            return 'mb-3 mt-6 whitespace-pre-wrap font-semibold leading-snug first:mt-0';
        case 'h5':
            return 'mb-2 mt-6 whitespace-pre-wrap font-semibold leading-snug first:mt-0';
        default:
            return 'mb-2 mt-5 whitespace-pre-wrap font-semibold leading-snug first:mt-0';
    }
}

function omitMarkdownDomProps(props = {}) {
    const { node, ordered, index, checked, sourcePosition, ...domProps } =
        props;
    void node;
    void ordered;
    void index;
    void checked;
    void sourcePosition;
    return domProps;
}

export function MarkDown(props) {
    const {
        className,
        children,
        variant,
        wrapperClassName,
        components: externalComponents,
    } = props;

    const SEMANTIC_HEADING_TAGS = new Set(['h1', 'h2']);
    const VISUAL_HEADING_TAGS = new Set(['h3', 'h4', 'h5', 'h6']);

    const markdownSource = useMemo(() => {
        if (children == null) return '';
        if (typeof children !== 'string' && typeof children !== 'number')
            return '';
        const applyMode =
            variant === 'h1' || variant === 'h2' ? 'never' : 'always';
        const source = enforceParagraphSpacing(String(children), {
            apply: applyMode,
        });
        if (variant === 'h1' || variant === 'h2') {
            return source
                .replace(/\r\n?/g, '\n')
                .replace(
                    /(^|\n)[ \t\u00a0]*(?:&nbsp;)?[ \t\u00a0]*(?=\n|$)/g,
                    ''
                );
        }
        return source;
    }, [children, variant]);
    const inlineFontFamilies = useMemo(
        () => extractInlineFontFamilies(markdownSource),
        [markdownSource]
    );

    const [linkColor, setLinkColor] = useState('link-light');
    const ref = useRef();

    useEffect(() => {
        if (!ref.current) return;
        let element = ref.current;
        let depth = 0;
        while (element && depth++ < 10) {
            const bgColor = window
                .getComputedStyle(element)
                .getPropertyValue('background-color');
            if (bgColor === 'rgba(0, 0, 0, 0)') {
                element = element.parentElement;
            } else {
                const light = getComputedStyle(
                    document.documentElement
                ).getPropertyValue('--primary-400');
                const dark = getComputedStyle(
                    document.documentElement
                ).getPropertyValue('--primary-700');
                const result = contrastColor({
                    bgColor: rgbToHex(bgColor),
                    fgLightColor: light,
                    fgDarkColor: dark,
                });
                if (result === dark) setLinkColor('link-dark');
                break;
            }
        }
    }, [ref.current]);

    useEffect(() => {
        const doc = ref.current?.ownerDocument;
        if (!doc?.head || !inlineFontFamilies.length) return;

        const loadedFamilies = new Set(
            Array.from(
                doc.head.querySelectorAll('link[data-markdown-font-family]')
            ).map((link) => link.dataset.markdownFontFamily)
        );

        inlineFontFamilies.forEach((family) => {
            const key = family.toLowerCase();
            if (loadedFamilies.has(key)) return;

            const weights = getKnownGoogleFontWeights(family);
            if (!weights.length) return;

            const familyParam = encodeURIComponent(family).replace(/%20/g, '+');
            const link = doc.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@${weights.join(
                ';'
            )}&display=swap`;
            link.dataset.markdownFontFamily = key;
            doc.head.appendChild(link);
            loadedFamilies.add(key);
        });
    }, [inlineFontFamilies]);

    const markdownComponents = useMemo(() => {
        const Paragraph = SEMANTIC_HEADING_TAGS.has(variant)
            ? ({ className, children, ...rest }) => {
                  const Element = variant; // 'h1'...'h6'
                  return (
                      <Element
                          {...omitMarkdownDomProps(rest)}
                          className={clsx(
                              className,
                              'leading-[1.08] [text-wrap:wrap]'
                          )}
                      >
                          {children}
                      </Element>
                  );
              }
            : VISUAL_HEADING_TAGS.has(variant)
            ? ({ className, children, ...rest }) => (
                  <div
                      {...omitMarkdownDomProps(rest)}
                      className={clsx(className, 'whitespace-pre-wrap')}
                  >
                      {children}
                  </div>
              )
            : ({ className, children, ...rest }) => (
                  <p
                      {...omitMarkdownDomProps(rest)}
                      className={clsx(
                          className,
                          'mb-4 whitespace-pre-wrap last:mb-0'
                      )}
                  >
                      {children}
                  </p>
              );

        const isExternal = (href = '') => /^(?:https?:)?\/\//i.test(href);

        return {
            p: Paragraph,
            h1: ({ className, children, ...rest }) => (
                <h1
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h1'))}
                >
                    {children}
                </h1>
            ),
            h2: ({ className, children, ...rest }) => (
                <h2
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h2'))}
                >
                    {children}
                </h2>
            ),
            h3: ({ className, children, ...rest }) => (
                <h3
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h3'))}
                >
                    {children}
                </h3>
            ),
            h4: ({ className, children, ...rest }) => (
                <h4
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h4'))}
                >
                    {children}
                </h4>
            ),
            h5: ({ className, children, ...rest }) => (
                <h5
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h5'))}
                >
                    {children}
                </h5>
            ),
            h6: ({ className, children, ...rest }) => (
                <h6
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, headingClasses('h6'))}
                >
                    {children}
                </h6>
            ),
            ul: ({ className, children, ...rest }) => (
                <ul
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(
                        className,
                        'my-4 list-disc space-y-1 pl-6 marker:text-current'
                    )}
                >
                    {children}
                </ul>
            ),
            ol: ({ className, children, ...rest }) => (
                <ol
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(
                        className,
                        'my-4 list-decimal space-y-1 pl-6 marker:text-current'
                    )}
                >
                    {children}
                </ol>
            ),
            li: ({ className, children, ...rest }) => (
                <li
                    {...omitMarkdownDomProps(rest)}
                    className={clsx(className, 'pl-1 [&>p]:mb-0')}
                >
                    {children}
                </li>
            ),

            a: ({ href, title, children, className, ...rest }) => {
                const openNew = title === 'newtab' || isExternal(href);
                const target = openNew ? '_blank' : undefined;
                const rel = openNew ? 'noopener noreferrer' : undefined;

                return (
                    <a
                        {...omitMarkdownDomProps(rest)}
                        href={href}
                        title={title}
                        target={target}
                        rel={rel}
                        className={clsx(className, linkColor)}
                    >
                        {children}
                    </a>
                );
            },
            ...externalComponents,
        };
    }, [variant, linkColor, externalComponents]);

    return (
        <div ref={ref} className={wrapperClassName}>
            <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkBreaks]}
                components={markdownComponents}
                className={className}
            >
                {markdownSource}
            </ReactMarkdown>
        </div>
    );
}

function rgbToHex(r, g, b) {
    // 1. Convert arguments to numbers. If they are undefined, null, or NaN,
    //    Number() will result in 0 or NaN. The `|| 0` catches all of them.
    const safeR = Number(r) || 0;
    const safeG = Number(g) || 0;
    const safeB = Number(b) || 0;

    // 2. Helper function to convert a number to a 2-digit hex string
    const toHex = (c) => {
        // Clamp the value between 0 and 255
        const clamped = Math.max(0, Math.min(c, 255));
        const hex = clamped.toString(16);
        // Pad with a '0' if it's only one digit (e.g., "A" -> "0A")
        return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(safeR)}${toHex(safeG)}${toHex(safeB)}`;
}
